import { EnglishAccentType, englishAccentRules, rhoticAccent } from "../../ruleSets/accentRules";
import { AllRules } from "../../ruleSets/allRules";
import { SetName, lexicalSets, lexicalSetsWikiOrdered } from "../../rules/findLexicalSets";
import { Say } from "../widgets/Say";

/** a table of accents versus lexical sets, showing the phonemes & letting you hear the results */
export function LexicalSets(props: {
  readonly accents: EnglishAccentType[],
  readonly accentColors: Record<string, string>,
  readonly vowelColor: string,
  readonly includeRefAccents: boolean,
}) {
  return (
    <table>
      <thead>
        <tr>
          <td></td>
          {props.includeRefAccents ? <><td>RP</td><td>GA</td></> : undefined}
          {props.accents.map(a => <td key={a} style={{ color: props.accentColors[a] }}>{a}</td>)}
        </tr>
      </thead>

      <tbody>
        {lexicalSetsWikiOrdered.map(name => {
          const ls = lexicalSets.filter(set => set.name === name)[0];
          return (<tr key={name}>
            <td>{name}</td>
            {props.includeRefAccents ? <ReferenceAccents uk={ls.uk} us={ls.us} vowelColor={props.vowelColor} /> : undefined}
            {props.accents.map(a => (<td key={a}>
              <AccentSet set={ls.name} accent={a} accentColors={props.accentColors} vowelColor={props.vowelColor} />
            </td>))}
          </tr>)
        })}
      </tbody>
    </table>
  );
}

function ReferenceAccents(props: {
  readonly uk: string,
  readonly us: string,
  readonly vowelColor: string,
}) {
  return (<>
    <td><Say textColor={props.vowelColor} say={`/${props.uk}/`}>{props.uk}</Say></td>
    <td><Say textColor={props.vowelColor} say={`/${props.us}/`}>{props.us}</Say></td>
  </>);
}

/** show/hear the sounds & sample word for one lexical set for one accent */
function AccentSet(props: {
  readonly set: SetName,
  readonly accent: EnglishAccentType,
  readonly accentColors: Record<string, string>,
  readonly vowelColor: string,
}) {
  const [show, say] = getPhones(props);

  const { afterRules } = AllRules.get().convert(props.set, 'English', props.accent)
  const word = `/${afterRules}/`;
  const color = props.accentColors[props.accent];

  return (<>
    <Say accent={props.accent} say={say} textColor={props.vowelColor}>{show}</Say>
    &nbsp;
    <Say accent={props.accent} textColor={color}>{word}</Say>
  </>);
}

/**
 * lookup the phones to use for a lexical set within an accent,
 * marking (r) as optional for non-rhotic accents
 * @returns [show, say]
 */
function getPhones(props: {
  readonly set: SetName,
  readonly accent: EnglishAccentType,
}): [string, string] {
  const sets = englishAccentRules[props.accent].lexicalSets;
  if (!sets) return ['?', ''];

  let show = sets[props.set];
  let say = sets[props.set];
  if (!rhoticAccent[props.accent]) {
    show = show.replace(/ɹ/g, '(ɹ)').replace(/ɾ/g, '(ɾ)');
    say = say.replace(/ɹ/g, '').replace(/ɾ/g, '');
  }
  return [`${show}`, `/${say}/`];
}
